import { AlarmScope, ApiClientProvider } from "@mb-pro-ui/utils";
import type { PropsWithChildren } from "react";
import { Redirect, useParams } from "wouter";
import { homeOrgName, useMe } from "../api/useMe";
import { FullPageSpinner } from "../components/States";
import { API_BASE_URL } from "../config";
import { OrgContext } from "./OrgContext";
import {
  orgNameToApiToken,
  orgNameToSegment,
  orgSegmentToName,
} from "./orgRouting";

// Mounts the request context of the `/:org` subtree and moves the app's one
// socket connection to that org's alarm room. `key` gives every org its own
// QueryClient: query keys carry no org, so a shared client would serve one
// org's cached data in another. The backend validates the org token against
// the session again; the check here only redirects early.
export default function OrgScope({ children }: PropsWithChildren) {
  const { org: segment = "" } = useParams<{ org: string }>();
  const { data: me, isLoading } = useMe();

  if (isLoading || !me) {
    return <FullPageSpinner />;
  }

  const allowedOrgs = me["allowed-orgs"] ?? [];
  const homeSegment = orgNameToSegment(homeOrgName(me));
  const allowed =
    segment === homeSegment ||
    allowedOrgs.some((entry) => orgNameToSegment(entry["org-name"]) === segment);
  if (!allowed) {
    return <Redirect to={`~/${homeSegment}`} replace />;
  }

  const homeName = homeOrgName(me) ?? "";
  const choices = [
    { segment: homeSegment, name: homeName, isHome: true },
    ...allowedOrgs
      .filter((entry) => orgNameToSegment(entry["org-name"]) !== homeSegment)
      .map((entry) => ({
        segment: orgNameToSegment(entry["org-name"]),
        name: entry["org-name"] ?? "",
        isHome: false,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  ];

  const name = orgSegmentToName(segment);
  const token = orgNameToApiToken(name);
  return (
    <AlarmScope org={token}>
      <ApiClientProvider key={segment} baseUrl={API_BASE_URL} orgName={token}>
        <OrgContext.Provider
          value={{ segment, name, isHome: segment === homeSegment, choices }}
        >
          {children}
        </OrgContext.Provider>
      </ApiClientProvider>
    </AlarmScope>
  );
}
